/* Share unsent product selections across all storefront controls. */
(() => {
  'use strict';
  if (window.asmSelectionMemory) return;
  const storageKey = 'asm-product-selections-v1';
  const panels = '.asm-product,.asm-card-purchase,product-info[data-product-handle]';
  let state, applying = false, queued = false, started = false;
  const panelState = new WeakMap();
  const wrappers = new Map();
  const id = value => /^\d+$/.test(String(value || '')) && Number.isSafeInteger(Number(value)) && Number(value) > 0 ? String(value) : '';
  const quantity = value => Number.isInteger(Number(value)) && Number(value) >= 1 && Number(value) <= 999 ? Number(value) : null;
  const handleOf = product => {
    const handle = typeof product === 'string' ? product : product && product.handle;
    return typeof handle === 'string' && /^[\p{L}\p{N}][\p{L}\p{N}-]{0,199}$/u.test(handle) ? handle : '';
  };
  function empty() { return { version: 1, products: Object.create(null), variants: Object.create(null) }; }
  function parse(raw) {
    const next = empty();
    try {
      const value = JSON.parse(raw);
      if (!value || value.version !== 1) return next;
      Object.entries(value.variants || {}).slice(-6000).forEach(([key, entry]) => {
        if (id(key) && entry && quantity(entry.quantity) !== null && handleOf(entry.handle)) {
          next.variants[key] = { handle: entry.handle, quantity: Number(entry.quantity), time: Number(entry.time) || 0 };
        }
      });
      Object.entries(value.products || {}).slice(-3000).forEach(([key, entry]) => {
        if (handleOf(key) && entry && id(entry.variantId) && next.variants[String(entry.variantId)]?.handle === key) {
          next.products[key] = { variantId: String(entry.variantId), time: Number(entry.time) || 0 };
        }
      });
    } catch (_) {}
    return next;
  }
  function load() {
    if (!state) {
      try { state = parse(localStorage.getItem(storageKey)); } catch (_) { state = empty(); }
    }
    return state;
  }
  function save() {
    const data = load();
    [['variants', 6000], ['products', 3000]].forEach(([key, limit]) => {
      const entries = Object.entries(data[key]);
      if (entries.length > limit) entries.sort((a, b) => a[1].time - b[1].time).slice(0, entries.length - limit).forEach(([name]) => delete data[key][name]);
    });
    try { localStorage.setItem(storageKey, JSON.stringify(data)); } catch (_) {}
  }
  function get(product, variantId) {
    const handle = handleOf(product), data = load();
    const key = variantId === undefined ? data.products[handle]?.variantId : id(variantId);
    const entry = key && data.variants[key];
    return handle && entry?.handle === handle ? { variantId: key, quantity: entry.quantity } : null;
  }
  function notify(handle, variantId, amount) {
    document.dispatchEvent(new CustomEvent('asm:product-selection-change', { detail: { handle, variantId, quantity: amount } }));
  }
  function remember(product, variantId, amount) {
    if (applying) return;
    const handle = handleOf(product), key = id(variantId), count = quantity(amount);
    if (!handle || !key || count === null) return;
    const data = load(), old = data.variants[key];
    if (old?.handle === handle && old.quantity === count && data.products[handle]?.variantId === key) return;
    const time = Date.now();
    data.variants[key] = { handle, quantity: count, time };
    data.products[handle] = { variantId: key, time };
    save();
    refresh();
    notify(handle, key, count);
  }
  function urlHandle(href) {
    try { return handleOf(decodeURIComponent(new URL(href, location.href).pathname.split('/products/')[1]?.split('/')[0] || '')); } catch (_) { return ''; }
  }
  function panelHandle(panel) {
    if (panel.matches('product-info')) return handleOf(panel.dataset.productHandle);
    const title = panel.querySelector('.asm-product__title[href],.asm-product__title a,.asm-card-title__link,.card__heading a');
    return urlHandle(title?.href) || handleOf(panel.dataset.productHandle);
  }
  function fieldOf(panel) {
    return panel.matches('product-info') ? panel.querySelector('.product-form__quantity .quantity__input') : panel.querySelector('.asm-card-qty input,.asm-qty input,.asm-card-qty span,.asm-qty span');
  }
  function readField(field) { return field && ('value' in field ? field.value : field.textContent); }
  function bound(field, amount) {
    const minimum = Math.max(1, Number(field?.min || field?.dataset.min) || 1);
    const maximum = Math.min(999, Number(field?.max) || 999);
    const step = Math.max(1, Number(field?.step) || 1);
    const clipped = Math.max(minimum, Math.min(maximum, amount));
    return Math.max(minimum, clipped - (clipped - minimum) % step);
  }
  function restoreEvent(element, bubbles = true) {
    const event = new Event('change', { bubbles });
    event.asmSelectionRestore = true;
    element.dispatchEvent(event);
  }
  function setQuantity(panel, amount) {
    const field = fieldOf(panel);
    if (!field) return;
    const value = String(bound(field, amount));
    if (String(readField(field)) !== value) {
      if ('value' in field) field.value = value; else field.textContent = value;
      restoreEvent(field);
    }
    panel.querySelectorAll('form[action*="/cart/add"] [name="quantity"],[data-asm-form-qty]').forEach(input => { if (input.value !== value) input.value = value; });
    if (!panel.matches('product-info')) {
      const minus = panel.querySelector('.asm-card-qty button:first-child,.asm-qty button:first-child');
      if (minus) { minus.disabled = Number(value) <= 1; minus.setAttribute('aria-disabled', String(Number(value) <= 1)); }
    }
  }
  function variantsOf(panel) {
    try { return JSON.parse(panel.querySelector('[data-asm-selection-variants]')?.textContent || '[]'); } catch (_) { return []; }
  }
  function selectedProductVariant(panel) { return id(panel.querySelector('product-form input[name="id"],form[action*="/cart/add"] input[name="id"]')?.value); }
  function pickerOf(panel) { return Array.from(panel.querySelectorAll('variant-selects')).find(picker => getComputedStyle(picker).display !== 'none'); }
  function productOptions(panel) {
    return Array.from(pickerOf(panel)?.querySelectorAll('.product-form__input') || []).map(group => group.querySelector('select')?.value || group.querySelector('input:checked')?.value || '');
  }
  function optionVariant(panel) {
    const options = productOptions(panel);
    return variantsOf(panel).find(variant => variant.available !== false && variant.options?.length === options.length && variant.options.every((value, index) => value === options[index]));
  }
  function applyProduct(panel, handle) {
    const saved = get(handle), current = selectedProductVariant(panel);
    const info = panelState.get(panel) || {};
    if (saved && saved.variantId !== current) {
      const variant = variantsOf(panel).find(item => id(item.id) === saved.variantId && item.available !== false);
      if (!variant) {
        const exact = get(handle, current);
        if (exact) setQuantity(panel, exact.quantity);
        return;
      }
      if (info.pending === saved.variantId) return;
      const groups = Array.from(pickerOf(panel)?.querySelectorAll('.product-form__input') || []);
      if (groups.length !== variant.options?.length) return;
      const controls = groups.map((group, index) => group.querySelector('select') || Array.from(group.querySelectorAll('input[type="radio"]')).find(input => input.value === variant.options[index]));
      if (controls.some((control, index) => !control || control.tagName === 'SELECT' && !Array.from(control.options).some(option => option.value === variant.options[index]))) return;
      info.pending = saved.variantId;
      panelState.set(panel, info);
      controls.forEach((control, index) => {
        if (control.tagName === 'SELECT') {
          control.value = variant.options[index];
          Array.from(control.options).forEach(option => {
            if (option.value === control.value) option.setAttribute('selected', 'selected');
            else option.removeAttribute('selected');
          });
        } else control.checked = true;
        if (index < controls.length - 1) restoreEvent(control, false);
      });
      restoreEvent(controls[controls.length - 1]);
      return;
    }
    if (info.pending && info.pending !== current) return;
    info.pending = '';
    panelState.set(panel, info);
    const exact = get(handle, current);
    if (exact) setQuantity(panel, exact.quantity);
  }
  function applyPanel(panel) {
    if (applying || panel.closest('[data-asm-fast-order]') || panel.matches('product-info') && panel.style.display === 'none') return;
    const handle = panelHandle(panel);
    if (!handle) return;
    applying = true;
    try {
      if (panel.matches('product-info')) { applyProduct(panel, handle); return; }
      const select = panel.querySelector('select[name="id"],select[data-asm-variant]');
      if (!select || !id(select.value)) return;
      const signature = Array.from(select.options, option => option.value).join('|');
      const previous = panelState.get(panel), preferred = get(handle);
      if (preferred && select.value !== preferred.variantId) {
        const option = Array.from(select.options).find(item => item.value === preferred.variantId && !item.disabled);
        if (option) { select.value = preferred.variantId; restoreEvent(select); }
        else if (previous && previous.handle !== handle) return;
      }
      const exact = get(handle, select.value);
      if (exact) setQuantity(panel, exact.quantity);
      else if (previous && (previous.handle !== handle || previous.signature !== signature)) setQuantity(panel, 1);
      const hidden = panel.querySelector('form[action*="/cart/add"] input[name="id"],[data-asm-variant-id]');
      if (hidden && hidden.value !== select.value) hidden.value = select.value;
      panelState.set(panel, { handle, signature });
    } finally { applying = false; }
  }
  function panelFor(element) { return element?.closest(panels); }
  function rememberPanel(panel) {
    if (!panel || panel.closest('[data-asm-fast-order]')) return;
    const variant = panel.matches('product-info') ? panelState.get(panel)?.pending || selectedProductVariant(panel) : id(panel.querySelector('select[name="id"],select[data-asm-variant]')?.value);
    remember(panelHandle(panel), variant, readField(fieldOf(panel)));
  }
  function wrapFunctions() {
    ['asmChangeQuantity', 'asmSaveQuantity', 'asmResetQuantities'].forEach(name => {
      const original = window[name];
      if (typeof original !== 'function' || wrappers.get(name) === original) return;
      const wrapped = function (...args) {
        if (name === 'asmResetQuantities') { refresh(); return; }
        const valid = name !== 'asmSaveQuantity' || quantity(args[0]?.value) !== null;
        const result = original.apply(this, args);
        if (!applying && valid && name !== 'asmResetQuantities') rememberPanel(panelFor(args[0]));
        return result;
      };
      wrappers.set(name, wrapped);
      window[name] = wrapped;
    });
  }
  function refresh(scope = document) {
    if (applying) return;
    wrapFunctions();
    if (scope.matches?.(panels)) applyPanel(scope);
    scope.querySelectorAll?.(panels).forEach(applyPanel);
  }
  function schedule() {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => { queued = false; refresh(); });
  }
  function seedCart(cart) {
    const data = load();
    let changed = false;
    (cart?.items || []).forEach(item => {
      const handle = handleOf(item.handle) || urlHandle(item.url), key = id(item.variant_id || item.id), count = quantity(item.quantity);
      if (!handle || !key || count === null || data.variants[key]) return;
      data.variants[key] = { handle, quantity: count, time: Date.now() };
      if (!data.products[handle]) data.products[handle] = { variantId: key, time: Date.now() };
      changed = true;
    });
    if (changed) save();
  }
  function capture(event) {
    if (applying || event.asmSelectionRestore || !(event.target instanceof Element) || event.target.closest('[data-asm-fast-order]')) return;
    const element = event.target, panel = panelFor(element);
    if (panel && element.matches('select[name="id"],select[data-asm-variant]') && event.type === 'change') {
      const handle = panelHandle(panel), key = id(element.value), saved = get(handle, key);
      applying = true;
      try { setQuantity(panel, saved?.quantity || 1); } finally { applying = false; }
      rememberPanel(panel);
    } else if (panel?.matches('product-info') && element.closest('variant-selects') && event.type === 'change') {
      const variant = optionVariant(panel);
      if (!variant) return;
      const handle = panelHandle(panel), key = id(variant.id), saved = get(handle, key);
      panelState.set(panel, { pending: key });
      remember(handle, key, saved?.quantity || 1);
    } else if (panel && element === fieldOf(panel) && (event.isTrusted || !panel.matches('product-info'))) {
      rememberPanel(panel);
    } else if (element.matches('.quantity__input[data-quantity-variant-id]') && event.isTrusted) {
      const row = element.closest('tr,.cart-item'), link = row?.querySelector('a[href*="/products/"]');
      remember(urlHandle(link?.href), element.dataset.quantityVariantId, element.value);
    } else if (panel?.matches('product-info') && element.matches('input[name="id"]') && event.type === 'change') schedule();
  }
  function start() {
    if (started) return;
    started = true;
    load();
    refresh();
    document.addEventListener('input', capture, true);
    document.addEventListener('change', capture, true);
    document.addEventListener('click', event => {
      if (applying || !(event.target instanceof Element)) return;
      const button = event.target.closest('product-info quantity-input button');
      if (button) rememberPanel(panelFor(button));
    });
    new MutationObserver(changes => {
      if (changes.some(change => change.type === 'attributes' || Array.from(change.addedNodes).some(node => node.nodeType === 1))) schedule();
    }).observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['href', 'min', 'max', 'step', 'data-product-handle'] });
    document.addEventListener('shopify:section:load', schedule);
    document.addEventListener('product-info:loaded', schedule);
    window.addEventListener('pageshow', schedule);
    window.addEventListener('storage', event => {
      if (event.key !== storageKey) return;
      const before = load();
      state = parse(event.newValue);
      refresh();
      Object.keys(state.products).forEach(handle => {
        const saved = get(handle), previous = before.variants[before.products[handle]?.variantId];
        if (saved && (saved.variantId !== before.products[handle]?.variantId || saved.quantity !== previous?.quantity)) notify(handle, saved.variantId, saved.quantity);
      });
    });
  }
  window.asmSelectionMemory = { isEnabled: () => true, get, remember, refresh, applyPanel, seedCart };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
